import React from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Skeleton,
  alpha,
  useTheme,
} from '@mui/material';

function MacroOutlookSkeleton() {
  const theme = useTheme();

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        pb: 6
      }}
    >
      {/* Header Section */}
      <Container maxWidth="xl" sx={{ pt: 1, pb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5 }} />
            <Box>
              <Skeleton variant="text" width={260} height={24} />
              <Skeleton variant="text" width={200} height={14} sx={{ mt: 0.5 }} />
              <Skeleton variant="text" width={120} height={12} sx={{ mt: 0.25 }} />
            </Box>
          </Box>
          <Skeleton variant="rounded" width={60} height={24} sx={{ borderRadius: 4 }} />
        </Box>
      </Container>

      {/* Main Content Container */}
      <Container maxWidth="xl">
        {/* SECTION 1: MACRO REGIME */}
        <Box sx={{ mb: 4 }}>
          <Card 
            sx={{ 
              height: '100%', 
              width: '100%',
              border: '1px solid #e0e0e0',
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              borderRadius: '4px'
            }}
          >
            <CardContent sx={{ p: 3 }}>
              {/* Header */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Skeleton variant="text" width={140} height={20} />
                <Skeleton variant="rounded" width={120} height={28} sx={{ borderRadius: 4 }} />
              </Box>

              {/* Current Status Box */}
              <Box 
                mb={3} 
                p={2.5} 
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}
              >
                {/* Left: Regime Name */}
                <Box>
                  <Skeleton variant="text" width={180} height={20} sx={{ mb: 0.5 }} />
                  <Skeleton variant="text" width={200} height={56} />
                </Box>
                
                {/* Right: Metrics */}
                <Box display="flex" gap={4} alignItems="center">
                  <Box textAlign="right">
                    <Skeleton variant="text" width={60} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width={80} height={40} />
                  </Box>
                  
                  <Box 
                    sx={{ 
                      width: '1px', 
                      height: '40px', 
                      bgcolor: 'divider' 
                    }} 
                  />
                  
                  <Box textAlign="right">
                    <Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
                    <Skeleton variant="text" width={70} height={40} />
                  </Box>
                </Box>
              </Box>

              {/* Trend Chart */}
              <Box>
                <Skeleton variant="text" width={200} height={20} sx={{ mb: 1.5 }} />
                <Skeleton variant="rectangular" width="100%" height={220} sx={{ borderRadius: 1 }} />
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* SECTION 2: NEWS SHOCK */}
        <Box sx={{ mb: 4 }}>
          <Card sx={{ height: '100%', width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={180} height={28} />
                </Box>
                <Skeleton variant="rounded" width={160} height={32} sx={{ borderRadius: 4 }} />
              </Box>

              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 2 }} />

              <Skeleton variant="rectangular" width="100%" height={280} sx={{ borderRadius: 1 }} />
            </CardContent>
          </Card>
        </Box>

        {/* SECTION 3: GATING WEIGHTS */}
        <Box sx={{ mb: 4 }}>
          <Card sx={{ height: '100%', width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Skeleton variant="circular" width={24} height={24} />
                  <Skeleton variant="text" width={160} height={28} />
                </Box>
                <Box display="flex" gap={1}>
                  <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 4 }} />
                  <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 4 }} />
                </Box>
              </Box>

              <Skeleton variant="rounded" width="100%" height={56} sx={{ mb: 2, borderRadius: 1 }} />

              <Skeleton variant="rectangular" width="100%" height={250} sx={{ borderRadius: 1 }} />
            </CardContent>
          </Card>
        </Box>

        {/* SECTION 4: FORECAST */}
        <Box sx={{ mb: 4 }}>
          <Card sx={{ height: '100%', width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={200} height={28} />
              </Box>

              <Box display="flex" gap={1} mb={2}>
                <Skeleton variant="rounded" width={120} height={24} sx={{ borderRadius: 4 }} />
                <Skeleton variant="rounded" width={120} height={24} sx={{ borderRadius: 4 }} />
                <Skeleton variant="rounded" width={120} height={24} sx={{ borderRadius: 4 }} />
              </Box>

              <Skeleton variant="rectangular" width="100%" height={280} sx={{ borderRadius: 1 }} />
            </CardContent>
          </Card>
        </Box>

        {/* SECTION 5: DRIVERS */}
        <Box sx={{ mb: 4 }}>
          <Card sx={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <CardContent sx={{ p: 3 }}>
              <Skeleton variant="text" width={240} height={28} sx={{ mb: 3 }} />

              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
                {/* Macro Drivers */}
                <Box flex={1}>
                  <Skeleton variant="text" width={140} height={24} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={300} sx={{ borderRadius: 1 }} />
                </Box>

                {/* News Drivers */}
                <Box flex={1}>
                  <Skeleton variant="text" width={160} height={24} sx={{ mb: 2 }} />
                  <Box>
                    {[1, 2, 3].map((i) => (
                      <Box key={i} mb={2} p={2} bgcolor="#f5f5f5" borderRadius={1}>
                        <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
                        <Box display="flex" gap={1}>
                          <Skeleton variant="rounded" width={100} height={24} sx={{ borderRadius: 4 }} />
                          <Skeleton variant="rounded" width={120} height={24} sx={{ borderRadius: 4 }} />
                          <Skeleton variant="rounded" width={80} height={24} sx={{ borderRadius: 4 }} />
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* SECTION 6: SECTOR HEATMAP */}
        <Box sx={{ mb: 4 }}>
          <Card sx={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: '4px' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <Skeleton variant="circular" width={24} height={24} />
                <Skeleton variant="text" width={220} height={28} />
              </Box>

              <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3} width="100%">
                {/* Stacked Bar Chart */}
                <Box flex={1} minWidth={0}>
                  <Skeleton variant="text" width="70%" height={20} sx={{ mb: 2 }} />
                  <Skeleton variant="rectangular" width="100%" height={600} sx={{ borderRadius: 1 }} />
                </Box>

                {/* Ranking Sidebar */}
                <Box sx={{ width: { xs: '100%', md: '280px' }, flexShrink: 0 }}>
                  <Box mb={3}>
                    <Skeleton variant="rounded" width={100} height={24} sx={{ mb: 2, borderRadius: 4 }} />
                    {[1, 2, 3].map((i) => (
                      <Box 
                        key={i}
                        display="flex" 
                        alignItems="center" 
                        mb={1.5}
                        p={1.5}
                        sx={{
                          bgcolor: alpha(theme.palette.success.main, 0.05),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                        }}
                      >
                        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.5 }} />
                        <Skeleton variant="text" width="80%" height={20} />
                      </Box>
                    ))}
                  </Box>
                  
                  <Box>
                    <Skeleton variant="rounded" width={100} height={24} sx={{ mb: 2, borderRadius: 4 }} />
                    {[1, 2, 3].map((i) => (
                      <Box 
                        key={i}
                        display="flex" 
                        alignItems="center" 
                        mb={1.5}
                        p={1.5}
                        sx={{
                          bgcolor: alpha(theme.palette.error.main, 0.05),
                          borderRadius: 1,
                          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`
                        }}
                      >
                        <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1.5 }} />
                        <Skeleton variant="text" width="80%" height={20} />
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>

      {/* Animated shimmer effect */}
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: 3,
          background: 'linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.5), transparent)',
          animation: 'shimmer 2s infinite',
          zIndex: 9999,
          '@keyframes shimmer': {
            '0%': { transform: 'translateX(-100%)' },
            '100%': { transform: 'translateX(100%)' },
          },
        }}
      />
    </Box>
  );
}

export default MacroOutlookSkeleton;

